import { Check, Calendar, User, Briefcase, DollarSign, FileText } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Earning, EarningStatus, CheckType } from '@/types';

interface EarningDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  earning: Earning | null;
  onMarkPaid: (id: string) => void;
  isLoading?: boolean;
}

export default function EarningDetailsModal({
  isOpen,
  onClose,
  earning,
  onMarkPaid,
  isLoading = false,
}: EarningDetailsModalProps) {
  if (!earning) return null;

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCheckTypeLabel = (type?: string) => {
    return type === CheckType.STANDARD ? 'Standard' : type === CheckType.ENHANCED ? 'Enhanced' : type || 'N/A';
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <div className="py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-900">Earning Details</h2>
              <Badge
                variant="outline"
                className={`${
                  earning.status === EarningStatus.PAID
                    ? 'bg-green-50 text-green-700 border-green-100'
                    : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                } px-3 py-1 rounded-lg font-medium capitalize`}
              >
                {earning.status}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">Review and manage agent earnings</p>
          </div>

          {/* Amount Card */}
          <div className="bg-gradient-to-br from-[#FF6B35]/10 to-orange-50 rounded-xl p-6 border border-orange-100 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <p className="text-sm text-gray-600 font-medium">Amount Earned</p>
            </div>
            <p className="text-4xl font-bold text-gray-900">
              {formatCurrency(earning.amountEarned || earning.amount)}
            </p>
          </div>

          {/* Agent Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Agent Information</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Name</span>
                <span className="text-sm font-medium text-gray-900">
                  {earning.agent?.firstName} {earning.agent?.lastName}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email</span>
                <span className="text-sm font-medium text-gray-900">{earning.agent?.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Phone</span>
                <span className="text-sm font-medium text-gray-900">{earning.agent?.phoneNumber || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100 capitalize">
                  {earning.agent?.status || 'active'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Investigation Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Investigation Details</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Employee Name</span>
                <span className="text-sm font-medium text-gray-900">
                  {earning.employeeName || 
                    `${earning.investigation?.employeeFirstName || ''} ${earning.investigation?.employeeLastName || ''}`.trim() || 
                    'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Check Type</span>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100 capitalize">
                  {getCheckTypeLabel(earning.checkType || earning.investigation?.checkType)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Investigation Status</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100 capitalize">
                  {earning.investigation?.status || 'completed'}
                </Badge>
              </div>
              {earning.investigation?.reportUrl && (
                <div className="pt-2">
                  <a
                    href={earning.investigation.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#FF6B35] hover:text-[#FF5722] font-medium"
                  >
                    <FileText className="w-4 h-4" />
                    View Investigation Report
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Dates Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Timeline</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Date Submitted</span>
                <span className="text-sm font-medium text-gray-900">
                  {earning.dateSubmitted ? formatDateTime(earning.dateSubmitted) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Date Earned</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(earning.createdAt)}
                </span>
              </div>
              {earning.datePaid && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Date Paid</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDateTime(earning.datePaid)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Validation Notes */}
          {earning.investigation?.validationNotes && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Validation Notes</h3>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {earning.investigation.validationNotes}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isLoading}
            >
              Close
            </Button>
            {earning.status === EarningStatus.PENDING && (
              <Button
                onClick={() => onMarkPaid(earning.id)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
